import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Alert,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { ApiService } from '../services/api';
import AddUserDialog from '../components/AddUserDialog';
import UserCreatedSuccessDialog from '../components/UserCreatedSuccessDialog';
import SearchLlmSettings from '../components/settings/SearchLlmSettings';
import IndustryTagsSettings from '../components/settings/IndustryTagsSettings';

type ApiKeyId = 'gmo' | 'reoon' | 'gemini' | 'zai' | 'claude' | 'google_search' | 'youcom' | 'mailwhiz';

// Keys stored in admin_settings_llm (DB) can be set from this page. gmo/reoon/mailwhiz
// only come from each backend's .env file, so there's nothing safe to write here - they
// stay read-only/view-only.
const DB_BACKED_KEYS: ApiKeyId[] = ['gemini', 'zai', 'claude', 'google_search', 'youcom'];
const DB_FIELD_FOR: Partial<Record<ApiKeyId, string>> = {
  gemini: 'gemini_api_key',
  zai: 'zai_api_key',
  claude: 'claude_api_key',
  google_search: 'google_search_api_key',
  youcom: 'youcom_api_key',
};

const API_KEY_CARDS: { id: ApiKeyId; label: string; hint: string }[] = [
  { id: 'gmo', label: 'GMO API', hint: 'Company email enrich (GetMoreOutreach)' },
  { id: 'reoon', label: 'Reoon API', hint: 'Email verification' },
  { id: 'gemini', label: 'Gemini API', hint: 'Web Search (Surf) and Gemini analysis models' },
  { id: 'zai', label: 'Z.ai API', hint: 'Optional GLM models for cheaper analysis' },
  { id: 'claude', label: 'Claude API', hint: 'Optional Anthropic models for analysis, and for Website Finder search via Claude web search (Opus 5.5, costs more per search than Gemini/You.com)' },
  { id: 'google_search', label: 'Google Custom Search API', hint: 'Legacy CSE JSON API (optional)' },
  { id: 'youcom', label: 'You.com API', hint: 'Alternate Web Search provider for Surf' },
  { id: 'mailwhiz', label: 'MailWhiz API', hint: 'Campaign sending' },
];

const emptyApiKeys = (): Record<ApiKeyId, string> => ({
  gmo: '', reoon: '', gemini: '', zai: '', claude: '', google_search: '', youcom: '', mailwhiz: '',
});

const maskApiKey = (value: string): string => {
  const v = (value || '').trim();
  if (!v) return 'Not set';
  if (v.length <= 10) return `${v.slice(0, 2)}…${v.slice(-2)}`;
  return `${v.slice(0, 6)}…${v.slice(-4)}`;
};

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  status: number;
  role: number;
  roleName: string;
  tfaEnabled: boolean;
  lastLoggedIn?: string | null;
  profileImage?: string;
}

interface RoleChangeState {
  [userId: number]: number; // userId -> new role
}

interface StatusChangeState {
  [userId: number]: number; // userId -> new status
}

const SETTINGS_TABS = [
  'profile',
  'users',
  'database-config',
  'apis',
  'search-llm',
  'industry-tags'
];

const SettingsPage: React.FC = () => {
  const { user: currentUser, updateUser, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Find active settings tab based on URL path
  const pathParts = location.pathname.split('/');
  const subPath = pathParts[pathParts.length - 1];
  const activeTabIdx = SETTINGS_TABS.indexOf(subPath);
  const activeTab = activeTabIdx !== -1 ? activeTabIdx : 0;
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Add user dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdUserData, setCreatedUserData] = useState<{
    id?: number;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  } | null>(null);

  // 2FA setup dialog
  const [tfaDialogOpen, setTfaDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [tfaSecret, setTfaSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [tfaError, setTfaError] = useState<string | null>(null);
  const [tfaLoading, setTfaLoading] = useState(false);

  // Profile form state
  const [profileData, setProfileData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    profileImage: string;
  }>({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
    profileImage: currentUser?.profileImage || 'profile1-96X96.png',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileImageDialogOpen, setProfileImageDialogOpen] = useState(false);

  // Delete user confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Password reset dialog state
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [newResetPassword, setNewResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);


  // Role change state
  const [roleChanges, setRoleChanges] = useState<RoleChangeState>({});
  const [roleChangeLoading, setRoleChangeLoading] = useState<{ [userId: number]: boolean }>({});

  // Status change state
  const [statusChanges, setStatusChanges] = useState<StatusChangeState>({});
  const [statusChangeLoading, setStatusChangeLoading] = useState<{ [userId: number]: boolean }>({});

  // API keys state
  const [apiKeys, setApiKeys] = useState<Record<ApiKeyId, string>>(emptyApiKeys);
  const [apiKeysVisible, setApiKeysVisible] = useState<Record<ApiKeyId, boolean>>({
    gmo: false, reoon: false, gemini: false, zai: false, google_search: false, youcom: false, claude: false, mailwhiz: false,
  });
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [editingKeyId, setEditingKeyId] = useState<ApiKeyId | null>(null);
  const [editKeyValue, setEditKeyValue] = useState('');
  const [savingKeyId, setSavingKeyId] = useState<ApiKeyId | null>(null);

  // Database config state
  const [dbConfig, setDbConfig] = useState<Record<string, string | number | boolean>>({});
  const [dbConfigLoading, setDbConfigLoading] = useState(false);
  const [dbConfigError, setDbConfigError] = useState<string | null>(null);
  const [dbConfigVisible, setDbConfigVisible] = useState<Record<string, boolean>>({});

  // Handle role as both number and string (from localStorage) - memoized to prevent recalculation
  const userRole = useMemo(() => {
    return currentUser?.role !== undefined ? Number(currentUser.role) : undefined;
  }, [currentUser?.role]);
  
  // Role 0 = root, 1 = editor, 2 = viewer, 3 = admin
  const isEditor = useMemo(() => {
    return userRole === 1;
  }, [userRole]);
  
  const isAdmin = useMemo(() => {
    return currentUser?.isAdmin || userRole === 3;
  }, [currentUser?.isAdmin, userRole]);
  
  const isRoot = useMemo(() => {
    return currentUser?.isRoot || userRole === 0;
  }, [currentUser?.isRoot, userRole]);
  
  // Can see user management, database config, API settings: root, editor, admin (not viewer)
  const canSeeSettings = useMemo(() => {
    return userRole === 0 || userRole === 1 || userRole === 3;
  }, [userRole]);
  
  // Memoize loadUsers to prevent unnecessary re-renders
  const loadUsers = useCallback(async () => {
    if (!canSeeSettings) {
      // Viewer cannot see user management
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getUsers();
      setUsers(response.users);
      setSelectedUserIds([]);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [canSeeSettings]);

  // Refresh user data on mount to ensure we have the latest role information
  useEffect(() => {
    if (currentUser) {
      // Always refresh on Settings page load to get latest user data
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Don't auto-load users on mount - only load when User Management tab is clicked
  // This is handled in handleTabChange when activeTab === 1

  // Update profile data when currentUser changes - memoized to prevent unnecessary updates
  useEffect(() => {
    if (currentUser) {
      setProfileData(prev => {
        const newData = {
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          email: currentUser.email || '',
          username: currentUser.username || '',
          profileImage: currentUser.profileImage || 'profile1-96X96.png',
        };
        // Only update if data actually changed
        if (
          prev.firstName !== newData.firstName ||
          prev.lastName !== newData.lastName ||
          prev.email !== newData.email ||
          prev.username !== newData.username ||
          prev.profileImage !== newData.profileImage
        ) {
          return newData;
        }
        return prev;
      });
    } else {
      // If no user data, try to refresh it
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.email, currentUser?.username, currentUser?.profileImage]);

  // Refresh user data on mount if username is missing
  useEffect(() => {
    if (currentUser && !currentUser.username) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.username]);



  const handleCloseAddDialog = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  const handleAddUser = useCallback(async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  }) => {
    setError(null);
    setSuccess(null);
    
    if (!userData.email || !userData.password || !userData.firstName || !userData.lastName || !userData.username) {
      setError('All fields are required');
      return userData;
    }

    try {
      const response = await ApiService.createUser(userData);
      setSuccess('User created successfully');
      setAddDialogOpen(false);
      // Include user ID from response if available
      setCreatedUserData({
        ...userData,
        id: response.user?.id,
      });
      setSuccessDialogOpen(true);
      loadUsers();
      return {
        ...userData,
        id: response.user?.id,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
      return userData;
    }
  }, [loadUsers]);

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    // Prevent deleting root users
    if (userToDelete.role === 0) {
      setError('You cannot delete a root user.');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      return;
    }

    // Prevent non-root users from deleting admins
    if (userToDelete.role === 1 && !isRoot) {
      setError('You cannot delete another admin. Only root users can delete admins.');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      return;
    }

    setError(null);
    setSuccess(null);
    setDeleteDialogOpen(false);

    try {
      await ApiService.deleteUser(userToDelete.id);
      setSuccess('User deleted successfully');
      setUserToDelete(null);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handlePasswordResetClick = (user: User) => {
    setUserToResetPassword(user);
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
    let pass = '';
    for (let i = 0; i < 16; i++) {
      pass += chars[Math.floor(Math.random() * chars.length)];
    }
    setNewResetPassword(pass);
    setShowResetPassword(false);
    setPasswordResetSuccess(false);
    setPasswordResetDialogOpen(true);
  };

  const handlePasswordResetCancel = () => {
    setPasswordResetDialogOpen(false);
    setUserToResetPassword(null);
    setNewResetPassword('');
  };

  const handlePasswordResetConfirm = async () => {
    if (!userToResetPassword) return;
    setPasswordResetLoading(true);
    setError(null);
    try {
      await ApiService.updateUserPassword(userToResetPassword.id, newResetPassword);
      setPasswordResetSuccess(true);
      setSuccess(`Password for ${userToResetPassword.username} updated successfully.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: number) => {
    // Validate role value: 1 = editor, 2 = viewer, 3 = admin
    // Root cannot assign root role (0) - they can only assign editor, viewer, or admin
    // Editor can only assign viewer (2)
    // Admin can assign editor (1) or viewer (2), but not admin (3) or root (0)
    if (isEditor && newRole !== 2) {
      setError('Editors can only change roles to viewer.');
      return;
    }
    if (isAdmin && !isRoot && (newRole === 3 || newRole === 0)) {
      setError('Admins cannot assign admin or root roles. Only editor and viewer roles can be assigned.');
      return;
    }
    if (newRole !== 1 && newRole !== 2 && newRole !== 3) {
      setError('Invalid role value. Only Editor (1), Viewer (2), and Admin (3) can be assigned.');
      return;
    }

    setError(null);
    setSuccess(null);
    setRoleChangeLoading(prev => ({ ...prev, [userId]: true }));

    try {
      await ApiService.updateUserRole(userId, newRole);
      setSuccess('User role updated successfully');
      setRoleChanges(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
      // Reset role change on error
      setRoleChanges(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    } finally {
      setRoleChangeLoading(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    }
  };

  const handleStatusChange = async (userId: number, newStatus: number) => {
    // Validate status value: 0 = inactive, 1 = active
    if (newStatus !== 0 && newStatus !== 1) {
      setError('Invalid status value. Only Active (1) and Inactive (0) are allowed.');
      return;
    }

    setError(null);
    setSuccess(null);
    setStatusChangeLoading(prev => ({ ...prev, [userId]: true }));

    try {
      await ApiService.updateUserStatus(userId, newStatus);
      setSuccess('User status updated successfully');
      setStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
      // Reset status change on error
      setStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    } finally {
      setStatusChangeLoading(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const modifiableUserIds = users
        .filter(user => user.id !== currentUser?.id && user.role !== 0 && !(user.role === 3 && !isRoot) && !(user.role === 1 && isEditor))
        .map(u => u.id);
      setSelectedUserIds(modifiableUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectRow = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedUserIds(prev => [...prev, userId]);
    } else {
      setSelectedUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleBatchEnable = async () => {
    if (selectedUserIds.length === 0) return;
    setBatchActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await Promise.all(selectedUserIds.map(id => ApiService.updateUserStatus(id, 1)));
      setSuccess(`Successfully activated ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status in batch');
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleBatchDisable = async () => {
    if (selectedUserIds.length === 0) return;
    setBatchActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await Promise.all(selectedUserIds.map(id => ApiService.updateUserStatus(id, 0)));
      setSuccess(`Successfully deactivated ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status in batch');
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleBatchDeleteClick = () => {
    setBatchDeleteDialogOpen(true);
  };

  const handleBatchDeleteConfirm = async () => {
    if (selectedUserIds.length === 0) return;
    setBatchDeleteDialogOpen(false);
    setBatchActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await Promise.all(selectedUserIds.map(id => ApiService.deleteUser(id)));
      setSuccess(`Successfully deleted ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete users in batch');
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleBatchDeleteCancel = () => {
    setBatchDeleteDialogOpen(false);
  };

  const handleSetup2FA = async (user: User) => {
    setSelectedUser(user);
    setTfaError(null);
    setQrCode(null);
    setTfaSecret(null);
    setVerificationCode('');

    try {
      const response = await ApiService.setupUser2FA(user.id);
      setQrCode(response.qrCode);
      setTfaSecret(response.secret);
      setTfaDialogOpen(true);
    } catch (err) {
      setTfaError(err instanceof Error ? err.message : 'Failed to setup 2FA');
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || !selectedUser) {
      setTfaError('Please enter the verification code');
      return;
    }

    setTfaError(null);
    setTfaLoading(true);

    try {
      await ApiService.enableUser2FA(selectedUser.id, verificationCode);
      setSuccess('2FA enabled successfully');
      setTfaDialogOpen(false);
      setQrCode(null);
      setTfaSecret(null);
      setVerificationCode('');
      loadUsers();
    } catch (err) {
      setTfaError(err instanceof Error ? err.message : 'Failed to enable 2FA');
    } finally {
      setTfaLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    setProfileError(null);
    setProfileSuccess(null);
    setProfileLoading(true);

    if (!profileData.firstName || !profileData.lastName || !profileData.email || !profileData.username) {
      setProfileError('All fields are required');
      setProfileLoading(false);
      return;
    }

    try {
      const response = await ApiService.updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        username: profileData.username,
        profileImage: profileData.profileImage || 'profile1-96X96.png',
      });
      setProfileSuccess('Profile updated successfully');
      
      // Update the user in AuthContext
      if (response.user) {
        updateUser(response.user);
      }
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // For non-admin and non-root users, only allow tab 0 (Profile)
    if (!canSeeSettings && newValue > 0) {
      return;
    }
    
    let actualTabIndex = newValue;
    if (!canSeeSettings) {
      actualTabIndex = 0; // Force to Profile
    }
    
    navigate(`/settings/${SETTINGS_TABS[actualTabIndex]}`);
  };

  // Dynamically load settings data based on active tab state
  useEffect(() => {
    if (activeTab === 1 && canSeeSettings) {
      loadUsers();
    }
    if (activeTab === 2 && canSeeSettings) {
      loadDatabaseConfig();
    }
    if (activeTab === 3 && canSeeSettings) {
      loadApiKeys();
    }
  }, [activeTab, canSeeSettings]);

  useEffect(() => {
    // Automatically redirect base /settings requests to the profile sub-path
    if (location.pathname === '/settings' || location.pathname === '/settings/') {
      navigate('/settings/profile', { replace: true });
    }
  }, [location.pathname, navigate]);

  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const [outreachKeys, llm] = await Promise.all([
        ApiService.getOutreachApiKeys().catch(() => ({ gmo: '', reoon: '', mailwhiz: '' })),
        ApiService.getLlmSettings().catch(() => ({})),
      ]);
      setApiKeys({
        gmo: outreachKeys.gmo || '',
        reoon: outreachKeys.reoon || '',
        mailwhiz: outreachKeys.mailwhiz || '',
        gemini: llm.gemini_api_key || '',
        zai: llm.zai_api_key || '',
        claude: llm.claude_api_key || '',
        google_search: llm.google_search_api_key || '',
        youcom: llm.youcom_api_key || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setApiKeysLoading(false);
    }
  };

  const startEditApiKey = (id: ApiKeyId) => {
    setEditingKeyId(id);
    setEditKeyValue(apiKeys[id] || '');
  };

  const cancelEditApiKey = () => {
    setEditingKeyId(null);
    setEditKeyValue('');
  };

  const saveEditApiKey = async () => {
    if (!editingKeyId) return;
    const field = DB_FIELD_FOR[editingKeyId];
    if (!field) return;
    setSavingKeyId(editingKeyId);
    setError(null);
    try {
      await ApiService.updateLlmSettings({ [field]: editKeyValue.trim() });
      setApiKeys(prev => ({ ...prev, [editingKeyId]: editKeyValue.trim() }));
      setEditingKeyId(null);
      setEditKeyValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save API key');
    } finally {
      setSavingKeyId(null);
    }
  };

  const loadDatabaseConfig = async () => {
    setDbConfigLoading(true);
    setDbConfigError(null);
    try {
      const config = await ApiService.getDatabaseConfig();
      setDbConfig(config);
    } catch (err) {
      setDbConfigError(err instanceof Error ? err.message : 'Failed to load database configuration');
    } finally {
      setDbConfigLoading(false);
    }
  };

  const handleToggleApiKeyVisibility = (apiType: ApiKeyId) => {
    setApiKeysVisible(prev => ({
      ...prev,
      [apiType]: !prev[apiType]
    }));
  };

  const handleCopyApiKey = async (apiType: ApiKeyId) => {
    const apiKey = apiKeys[apiType];
    if (apiKey) {
      try {
        await navigator.clipboard.writeText(apiKey);
        setSuccess(`${API_KEY_CARDS.find(c => c.id === apiType)?.label || apiType} copied to clipboard`);
      } catch (err) {
        setError('Failed to copy API key');
      }
    }
  };

  const handleCopyConfigValue = async (key: string, value: string | number | boolean) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setSuccess(`${key} copied to clipboard`);
    } catch (err) {
      setError('Failed to copy value');
    }
  };

  const handleToggleConfigVisibility = (key: string) => {
    setDbConfigVisible(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      minHeight: '100vh',
      display: 'flex', 
      flexDirection: 'column', 
      overflowX: 'hidden',
      backgroundColor: 'white',
    }}>
      {/* Fixed Header Section */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            mb: 2, 
            flexShrink: 0,
          }}
        >
          Settings
        </Typography>

        {/* Only show success alerts at top - errors are shown within each tab's content area */}
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2, borderRadius: 2 }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}
      </Box>

      {/* Settings Layout with Left Sidebar Tabs - Scrollable Content Area */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left Sidebar Tabs - Fixed */}
        <Paper 
          elevation={0}
          sx={{ 
            minWidth: 160,
            width: 160,
            flexShrink: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
            overflow: 'hidden',
            height: 'fit-content',
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="settings tabs"
            orientation="vertical"
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                left: 0,
                width: 3,
                backgroundColor: '#1976d2',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                alignItems: 'flex-start',
                minHeight: 48,
                padding: '12px 16px',
                color: '#64748b',
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  color: '#1976d2',
                  backgroundColor: '#f0f7ff',
                  fontWeight: 600,
                },
                '&:hover': {
                  backgroundColor: '#f8fafc',
                },
                transition: 'all 0.2s ease-in-out',
              }
            }}
          >
            <Tab label="Profile" />
            {canSeeSettings && <Tab label="User Management" />}
            {canSeeSettings && <Tab label="Database Config" />}
            {canSeeSettings && <Tab label="APIs" />}
            {canSeeSettings && <Tab label="Search & LLM" />}
            {canSeeSettings && <Tab label="Industry Tags" />}
            {/* Add more tabs here in the future */}
          </Tabs>
        </Paper>

        {/* Right Content Area */}
        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Profile Tab - Available to all users */}
          {activeTab === 0 && (
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, flexShrink: 0 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: '#1a202c',
                mb: 0.5 
              }}
            >
              Profile Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update your personal information and account details.
            </Typography>

            {profileError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setProfileError(null)}>
                {profileError}
              </Alert>
            )}

            {profileSuccess && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setProfileSuccess(null)}>
                {profileSuccess}
              </Alert>
            )}
          </CardContent>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 2, pb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Profile Image
                </Typography>
                <Box
                  onClick={() => setProfileImageDialogOpen(true)}
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    border: '3px solid #e0e0e0',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#1976d2',
                      '& .edit-overlay': {
                        opacity: 1,
                      }
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={`/${profileData.profileImage}`}
                    alt="Current Profile"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Box
                    className="edit-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <EditIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleUpdateProfile}
                    disabled={profileLoading}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 1.5,
                      px: 3,
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      }
                    }}
                  >
                    {profileLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      )}

          {/* User Management Tab - Admin and Root only */}
          {activeTab === 1 && canSeeSettings && (
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1a202c',
                    mb: 0.5 
                  }}
                >
                  User Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage users, add new users, and configure 2FA settings.
                </Typography>
              </Box>
              {/* Editor cannot add users */}
              {!isEditor && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ 
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  }
                }}
              >
                Add User
              </Button>
              )}
            </Box>
          </CardContent>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              Failed to fetch
            </Alert>
          )}
          
          <Collapse in={selectedUserIds.length > 0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                boxShadow: '0 2px 10px rgba(22, 101, 52, 0.05)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', backgroundColor: '#dcfce7', fontSize: '0.75rem' }}>
                  {selectedUserIds.length}
                </Box>
                user{selectedUserIds.length > 1 ? 's' : ''} selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  disabled={batchActionLoading}
                  onClick={handleBatchEnable}
                  sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 500, backgroundColor: 'white', '&:hover': { backgroundColor: '#f0fdf4' } }}
                >
                  Make Active
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={batchActionLoading}
                  onClick={handleBatchDisable}
                  sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 500, backgroundColor: 'white', '&:hover': { backgroundColor: '#fffbeb' } }}
                >
                  Make Inactive
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={batchActionLoading}
                  onClick={handleBatchDeleteClick}
                  sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 500, boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)', '&:hover': { boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' } }}
                >
                  Delete Selected
                </Button>
              </Box>
            </Box>
          </Collapse>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer 
              component={Paper}
              elevation={0}
              sx={{ 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
                overflowY: 'hidden',
                maxWidth: '100%'
              }}
            >
              <Table sx={{ minWidth: 950 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    {canSeeSettings && !isEditor && (
                      <TableCell padding="checkbox" sx={{ pl: 2, py: 1, width: 50 }}>
                        <Checkbox
                          indeterminate={
                            selectedUserIds.length > 0 &&
                            selectedUserIds.length < users.filter(user => user.id !== currentUser?.id && user.role !== 0 && !(user.role === 3 && !isRoot) && !(user.role === 1 && isEditor)).length
                          }
                          checked={
                            users.length > 0 &&
                            selectedUserIds.length === users.filter(user => user.id !== currentUser?.id && user.role !== 0 && !(user.role === 3 && !isRoot) && !(user.role === 1 && isEditor)).length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 120, maxWidth: 150 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 150, maxWidth: 200 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 100, maxWidth: 130 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 100, maxWidth: 120 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 100, maxWidth: 120 }}>2FA Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 100, maxWidth: 120 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 150, maxWidth: 200 }}>Last Logged In</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 1, minWidth: 100, maxWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canSeeSettings && !isEditor ? 9 : 8} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, index) => {
                      const isUserModifiable = user.id !== currentUser?.id && 
                                               user.role !== 0 && 
                                               !(user.role === 3 && !isRoot) && 
                                               !(user.role === 1 && isEditor);
                      const isRowSelected = selectedUserIds.includes(user.id);
                      return (
                        <TableRow 
                          key={user.id}
                          sx={{ 
                            backgroundColor: isRowSelected ? '#f0fdf4' : 'transparent',
                            '&:hover': {
                              backgroundColor: isRowSelected ? '#ecfdf5' : '#f8fafc',
                            },
                            transition: 'background-color 0.2s ease-in-out',
                            borderBottom: index < users.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          {canSeeSettings && !isEditor && (
                            <TableCell padding="checkbox" sx={{ pl: 2, py: 1.5 }}>
                              <Checkbox
                                checked={isRowSelected}
                                disabled={!isUserModifiable}
                                onChange={(e) => handleSelectRow(user.id, e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ py: 1.5, minWidth: 120, maxWidth: 150 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.875rem' }}>
                              {user.firstName} {user.lastName}
                            </Typography>
                          </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 150, maxWidth: 200 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 100, maxWidth: 130 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {user.username}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 100, maxWidth: 120 }}>
                          {user.id === currentUser?.id ? (
                            <Chip
                              label={user.roleName}
                              color={user.role === 0 ? 'error' : user.role === 1 ? 'primary' : 'default'}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : user.role === 0 ? (
                            <Chip
                              label={user.roleName}
                              color="error"
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : (user.role === 0) ? (
                            // Root user - cannot change role
                            <Chip
                              label={user.roleName}
                              color="error"
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : (user.role === 3 && !isRoot) ? (
                            // Admin user - only root can change
                            <Chip
                              label={user.roleName}
                              color="primary"
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : (user.role === 1 && isEditor) ? (
                            // Editor user - editor cannot change other editors
                            <Chip
                              label={user.roleName}
                              color="primary"
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : (
                            // Can change role - show dropdown
                            <FormControl size="small" sx={{ minWidth: 90, maxWidth: 110 }}>
                              <Select
                                value={
                                  roleChanges[user.id] !== undefined 
                                    ? roleChanges[user.id] 
                                    : (user.role === 0 || user.role === 1 || user.role === 2 || user.role === 3 ? user.role : 2)
                                }
                                onChange={(e) => {
                                  const newRole = typeof e.target.value === 'string' ? parseInt(e.target.value, 10) : e.target.value as number;
                                  const currentRole = user.role === 0 || user.role === 1 || user.role === 2 || user.role === 3 ? user.role : 2;
                                  if (newRole !== currentRole) {
                                    setRoleChanges(prev => ({ ...prev, [user.id]: newRole }));
                                    handleRoleChange(user.id, newRole);
                                  }
                                }}
                                disabled={roleChangeLoading[user.id]}
                                sx={{
                                  fontSize: '0.75rem',
                                  height: '28px',
                                  '& .MuiSelect-select': {
                                    py: 0.5,
                                  }
                                }}
                              >
                                <MenuItem value={2}>Viewer</MenuItem>
                                {isEditor ? null : <MenuItem value={1}>Editor</MenuItem>}
                                {isRoot && <MenuItem value={3}>Admin</MenuItem>}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 100, maxWidth: 120 }}>
                          <Chip
                            label={user.tfaEnabled ? 'Enabled' : 'Not Enabled'}
                            color={user.tfaEnabled ? 'success' : 'default'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              height: '24px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 100, maxWidth: 120 }}>
                          {user.id === currentUser?.id ? (
                            <Chip
                              label={user.status === 1 ? 'Active' : 'Inactive'}
                              color={user.status === 1 ? 'success' : 'default'}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : user.role === 0 ? (
                            <Chip
                              label={user.status === 1 ? 'Active' : 'Inactive'}
                              color={user.status === 1 ? 'success' : 'default'}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ) : isRoot ? (
                            <FormControl size="small" sx={{ minWidth: 90, maxWidth: 110 }}>
                              <Select
                                value={
                                  statusChanges[user.id] !== undefined 
                                    ? statusChanges[user.id] 
                                    : (user.status === 0 || user.status === 1 ? user.status : 1)
                                }
                                onChange={(e) => {
                                  const newStatus = e.target.value as number;
                                  const currentStatus = user.status === 0 || user.status === 1 ? user.status : 1;
                                  if (newStatus !== currentStatus) {
                                    setStatusChanges(prev => ({ ...prev, [user.id]: newStatus }));
                                    handleStatusChange(user.id, newStatus);
                                  }
                                }}
                                disabled={statusChangeLoading[user.id]}
                                sx={{
                                  fontSize: '0.75rem',
                                  height: '28px',
                                  '& .MuiSelect-select': {
                                    py: 0.5,
                                  }
                                }}
                              >
                                <MenuItem value={1}>Active</MenuItem>
                                <MenuItem value={0}>Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={user.status === 1 ? 'Active' : 'Inactive'}
                              color={user.status === 1 ? 'success' : 'default'}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 150, maxWidth: 200 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {user.lastLoggedIn 
                              ? new Date(user.lastLoggedIn).toLocaleString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 100, maxWidth: 120 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleSetup2FA(user)}
                              disabled={
                                user.tfaEnabled || 
                                (user.role === 0 && user.id !== currentUser?.id)
                              }
                              title={
                                user.tfaEnabled 
                                  ? "2FA already enabled"
                                  : user.role === 0 && user.id !== currentUser?.id
                                  ? "Cannot setup 2FA for root users. Only root users can setup 2FA for themselves."
                                  : "Setup 2FA"
                              }
                              sx={{
                                color: (user.tfaEnabled || (user.role === 0 && user.id !== currentUser?.id)) ? 'text.disabled' : '#1976d2',
                                '&:hover': {
                                  backgroundColor: '#f0f7ff',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <SecurityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handlePasswordResetClick(user)}
                              disabled={!(isRoot || (isAdmin && user.role !== 0))}
                              title={
                                !(isRoot || (isAdmin && user.role !== 0))
                                  ? "Only root users can reset a root user's password"
                                  : "Reset Password"
                              }
                              sx={{
                                color: !(isRoot || (isAdmin && user.role !== 0)) ? 'text.disabled' : '#f59e0b',
                                '&:hover': {
                                  backgroundColor: '#fef3c7',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <VpnKeyIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            disabled={
                              user.id === currentUser?.id || 
                              (user.role === 0) || 
                              (user.role === 3 && !isRoot) ||
                              (user.role === 1 && isEditor)
                            }
                            title={
                              user.id === currentUser?.id 
                                ? "Cannot delete your own account" 
                                : user.role === 0
                                ? "Cannot delete a root user"
                                : user.role === 3 && !isRoot
                                ? "Only root users can delete admin users"
                                : user.role === 1 && isEditor
                                ? "Editors cannot delete other editors"
                                : "Delete user"
                            }
                            sx={{
                              color: (user.id === currentUser?.id || user.role === 0 || (user.role === 3 && !isRoot) || (user.role === 1 && isEditor)) ? 'text.disabled' : '#ef4444',
                              '&:hover': {
                                backgroundColor: '#fef2f2',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )})
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Box>
        </Card>
      )}

      {/* APIs Tab - Admin and Root only */}
      {activeTab === 3 && canSeeSettings && (
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, flexShrink: 0 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: '#1a202c',
                mb: 0.5 
              }}
            >
              API Keys Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keys for outbound services. Values are masked; View reveals the full key.
            </Typography>
          </CardContent>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
            {apiKeysLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {API_KEY_CARDS.map((card) => {
                  const raw = apiKeys[card.id] || '';
                  const shown = apiKeysVisible[card.id] ? (raw || 'Not set') : maskApiKey(raw);
                  const isEditable = DB_BACKED_KEYS.includes(card.id);
                  const isEditing = editingKeyId === card.id;
                  return (
                    <Grid item xs={12} key={card.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          backgroundColor: '#f8fafc'
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                            {card.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {card.hint}{!isEditable ? ' — set via that backend\'s .env file' : ''}
                          </Typography>
                        </Box>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoFocus
                              placeholder={`Paste the new ${card.label} key`}
                              value={editKeyValue}
                              onChange={(e) => setEditKeyValue(e.target.value)}
                              sx={{
                                flexGrow: 1,
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontFamily: 'monospace', fontSize: '0.875rem' },
                              }}
                            />
                            <Button
                              variant="contained"
                              onClick={saveEditApiKey}
                              disabled={savingKeyId === card.id}
                              sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1.5, px: 2 }}
                            >
                              {savingKeyId === card.id ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={cancelEditApiKey}
                              disabled={savingKeyId === card.id}
                              sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1.5, px: 2 }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              value={shown}
                              InputProps={{
                                readOnly: true,
                                sx: {
                                  backgroundColor: 'white',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  '& input': { cursor: 'default' }
                                }
                              }}
                              sx={{
                                flexGrow: 1,
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                              }}
                            />
                            <Button
                              variant="outlined"
                              startIcon={apiKeysVisible[card.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              onClick={() => handleToggleApiKeyVisibility(card.id)}
                              disabled={!raw}
                              sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1.5, px: 2 }}
                            >
                              {apiKeysVisible[card.id] ? 'Hide' : 'View'}
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => handleCopyApiKey(card.id)}
                              disabled={!raw}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderRadius: 1.5,
                                px: 2,
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                                '&:hover': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)' },
                              }}
                            >
                              Copy
                            </Button>
                            {isEditable && (
                              <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => startEditApiKey(card.id)}
                                sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1.5, px: 2 }}
                              >
                                {raw ? 'Change' : 'Set key'}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Card>
      )}

      {/* Database Config Tab - Admin and Root only */}
      {activeTab === 2 && canSeeSettings && (
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, flexShrink: 0 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: '#1a202c',
                mb: 0.5 
              }}
            >
              Database Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              View current database connection settings and configuration values.
            </Typography>

            {dbConfigError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setDbConfigError(null)}>
                {dbConfigError}
              </Alert>
            )}
          </CardContent>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
            {dbConfigLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {Object.entries(dbConfig).map(([key, value]) => (
                  <Grid item xs={12} key={key}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                          {key}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={dbConfigVisible[key] ? String(value) : '•'.repeat(String(value).length || 20)}
                          InputProps={{
                            readOnly: true,
                            sx: {
                              backgroundColor: 'white',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              '& input': {
                                cursor: 'default'
                              }
                            }
                          }}
                          sx={{
                            flexGrow: 1,
                            minWidth: 300,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={dbConfigVisible[key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          onClick={() => handleToggleConfigVisibility(key)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 1.5,
                            px: 2,
                          }}
                        >
                          {dbConfigVisible[key] ? 'Hide' : 'View'}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyConfigValue(key, value)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 1.5,
                            px: 2,
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            }
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Card>
      )}

          {/* Search & LLM Tab */}
          {activeTab === 4 && canSeeSettings && (
            <SearchLlmSettings />
          )}

          {/* Industry Tags Tab */}
          {activeTab === 5 && canSeeSettings && (
            <IndustryTagsSettings />
          )}
        </Box>
      </Box>

      {/* Add User Dialog */}
      <AddUserDialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        onAddUser={handleAddUser}
      />

      {/* User Created Success Dialog */}
      <UserCreatedSuccessDialog
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false);
          setCreatedUserData(null);
        }}
        userData={createdUserData}
        onSetup2FA={async (userId: number) => {
          const response = await ApiService.setupUser2FA(userId);
          return {
            qrCode: response.qrCode,
            secret: response.secret,
          };
        }}
        onEnable2FA={async (userId: number, code: string) => {
          await ApiService.enableUser2FA(userId, code);
          setSuccess('2FA enabled successfully');
          loadUsers();
        }}
      />

      {/* Delete User Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
          Delete User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this user?
          </Typography>
          {userToDelete && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f8fafc', 
              borderRadius: 1.5,
              mb: 2 
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {userToDelete.firstName} {userToDelete.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userToDelete.email}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Delete Users Confirmation Dialog */}
      <Dialog 
        open={batchDeleteDialogOpen} 
        onClose={handleBatchDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '400px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
          Delete Multiple Users
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the {selectedUserIds.length} selected user(s)?
          </Typography>
          <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
            This action cannot be undone and will permanently remove all selected user accounts from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleBatchDeleteCancel}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBatchDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              }
            }}
          >
            Delete Users
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog 
        open={passwordResetDialogOpen} 
        onClose={handlePasswordResetCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          {userToResetPassword && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Resetting password for <strong>{userToResetPassword.firstName} {userToResetPassword.lastName}</strong> ({userToResetPassword.username})
            </Typography>
          )}

          {passwordResetSuccess ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>
                Password updated successfully!
              </Alert>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#1e293b' }}>
                Please copy the new password below and share it with the user securely. You won't be able to view this password again.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                p: 1.5, 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0',
                borderRadius: 1.5,
                mb: 1
              }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, flex: 1, wordBreak: 'break-all', fontSize: '1rem', letterSpacing: '0.5px' }}>
                  {newResetPassword}
                </Typography>
                <IconButton 
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(newResetPassword);
                  }}
                  title="Copy to clipboard"
                  sx={{ color: '#64748b', '&:hover': { backgroundColor: '#e2e8f0' } }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showResetPassword ? 'text' : 'password'}
                  value={newResetPassword}
                  onChange={(e) => setNewResetPassword(e.target.value)}
                  size="small"
                  disabled={passwordResetLoading}
                  error={newResetPassword.trim().length < 6}
                  helperText={newResetPassword.trim().length < 6 ? 'Password must be at least 6 characters' : ''}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        disabled={passwordResetLoading}
                      >
                        {showResetPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    )
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => {
                  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
                  let pass = '';
                  for (let i = 0; i < 16; i++) {
                    pass += chars[Math.floor(Math.random() * chars.length)];
                  }
                  setNewResetPassword(pass);
                }}
                disabled={passwordResetLoading}
                sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 500 }}
              >
                Generate Strong Password
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          {passwordResetSuccess ? (
            <Button 
              onClick={handlePasswordResetCancel}
              variant="contained"
              color="primary"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 1.5,
                px: 3
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button 
                onClick={handlePasswordResetCancel}
                disabled={passwordResetLoading}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1.5,
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePasswordResetConfirm}
                variant="contained"
                color="primary"
                disabled={passwordResetLoading || newResetPassword.trim().length < 6}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  px: 3,
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  }
                }}
              >
                {passwordResetLoading ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog 
        open={tfaDialogOpen} 
        onClose={() => setTfaDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
          Setup 2FA for {selectedUser?.firstName} {selectedUser?.lastName}
        </DialogTitle>
        <DialogContent>
          {tfaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tfaError}
            </Alert>
          )}

          {qrCode && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Scan this QR code with your authenticator app:
              </Typography>
              <img src={qrCode} alt="2FA QR Code" style={{ maxWidth: '100%', marginTop: '16px' }} />
              {tfaSecret && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Manual entry key: {tfaSecret}
                </Typography>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter 6-digit code from authenticator"
            sx={{ mt: 2 }}
            disabled={tfaLoading}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setTfaDialogOpen(false)} 
            disabled={tfaLoading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEnable2FA} 
            variant="contained" 
            disabled={!verificationCode || tfaLoading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              px: 3,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              }
            }}
          >
            {tfaLoading ? <CircularProgress size={20} /> : 'Enable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Image Selection Dialog */}
      <Dialog
        open={profileImageDialogOpen}
        onClose={() => setProfileImageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem' }}>
          Select Profile Image
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', py: 2 }}>
            <Tooltip title="Pickett" arrow>
              <Box
                onClick={() => {
                  setProfileData({ ...profileData, profileImage: 'profile1-96X96.png' });
                  setProfileImageDialogOpen(false);
                }}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: profileData.profileImage === 'profile1-96X96.png' ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/profile1-96X96.png"
                  alt="Profile 1"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Niffler" arrow>
              <Box
                onClick={() => {
                  setProfileData({ ...profileData, profileImage: 'profile2-96X96.png' });
                  setProfileImageDialogOpen(false);
                }}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: profileData.profileImage === 'profile2-96X96.png' ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/profile2-96X96.png"
                  alt="Profile 2"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Thunderbird" arrow>
              <Box
                onClick={() => {
                  setProfileData({ ...profileData, profileImage: 'profile3-96X96.png' });
                  setProfileImageDialogOpen(false);
                }}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: profileData.profileImage === 'profile3-96X96.png' ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/profile3-96X96.png"
                  alt="Profile 3"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Occamy" arrow>
              <Box
                onClick={() => {
                  setProfileData({ ...profileData, profileImage: 'profile4-96X96.png' });
                  setProfileImageDialogOpen(false);
                }}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: profileData.profileImage === 'profile4-96X96.png' ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/profile4-96X96.png"
                  alt="Profile 4"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Matagot" arrow>
              <Box
                onClick={() => {
                  setProfileData({ ...profileData, profileImage: 'profile5-96X96.png' });
                  setProfileImageDialogOpen(false);
                }}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: profileData.profileImage === 'profile5-96X96.png' ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/profile5-96X96.png"
                  alt="Profile 5"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setProfileImageDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;

